// ====== HerpMembers API client ====== //
const API_BASE = "https://skbw5jni2i.execute-api.us-east-2.amazonaws.com/dev";

async function apiFetch(path, { method = "GET", body } = {}) {
  const opts = {
    method,
    headers: { "Content-Type": "application/json" },
  };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, opts);
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(`API error: ${msg}`);
  }
  return data;
}

const HerpMembers = {
  health() {
    return apiFetch("/members/health");
  },
  upsert({ member_Id, email, plan = "free", labels = [] } = {}) {
    if (!member_Id || !email) throw new Error("member_Id and email are required");
    return apiFetch("/members/upsert", {
      method: "POST",
      body: { member_Id, email, plan, labels },
    });
  },
  getByEmail(email) {
    if (!email) throw new Error("email required");
    const q = new URLSearchParams({ email });
    return apiFetch(`/members?${q.toString()}`);
  },
  getById(member_Id) {
    if (!member_Id) throw new Error("member_Id required");
    const q = new URLSearchParams({ member_Id });
    return apiFetch(`/members?${q.toString()}`);
  },
  getByPlan(plan) {
    if (!plan) throw new Error("plan required");
    const q = new URLSearchParams({ plan });
    return apiFetch(`/members?${q.toString()}`);
  },
};

window.HerpMembers = HerpMembers;

(() => {
  const config = window.hsEliteTrialConfig;
  const hasSignedInConfig = Boolean(config && config.loggedIn && config.email);
  const trialTriggerSelector = "[data-start-trial-popup]";
  if (window.location.pathname.startsWith("/apps/herpshipper/start-trial")) return;

  const accountKey = hasSignedInConfig
    ? String(config.email || "").trim().toLowerCase()
    : "";

  const dismissKey = `hsEliteTrialDismissed:${accountKey}`;
  const AUTO_PROMPT_REFRESH_INTERVAL = 7;
  let memberStatusPromise = null;

  const storageForDismissal = () => {
    try {
      return window.localStorage || window.sessionStorage || null;
    } catch (_error) {
      return null;
    }
  };

  const readDismissState = () => {
    const storage = storageForDismissal();
    if (!storage || !dismissKey) return null;

    try {
      const raw = storage.getItem(dismissKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return {
        skipAutoShows: Math.max(0, Number(parsed.skipAutoShows || 0)),
      };
    } catch (_error) {
      return null;
    }
  };

  const writeDismissState = (state) => {
    const storage = storageForDismissal();
    if (!storage || !dismissKey) return;

    try {
      storage.setItem(dismissKey, JSON.stringify({
        skipAutoShows: Math.max(0, Number(state?.skipAutoShows || 0)),
      }));
    } catch (_error) {
      // Ignore storage write failures so the trial prompt can still function.
    }
  };

  const resetAutoPromptCooldown = () => {
    writeDismissState({ skipAutoShows: AUTO_PROMPT_REFRESH_INTERVAL - 1 });
  };

  const consumeAutoPromptCooldown = () => {
    const current = readDismissState();
    if (!current?.skipAutoShows) return false;

    writeDismissState({ skipAutoShows: current.skipAutoShows - 1 });
    return true;
  };

  const injectStyles = () => {
    if (document.getElementById("hs-elite-trial-styles")) return;

    const style = document.createElement("style");
    style.id = "hs-elite-trial-styles";
    style.textContent = `
      .hs-elite-trial-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: grid;
        place-items: center;
        padding: 20px;
      }
      .hs-elite-trial-modal[hidden] { display: none; }
      .hs-elite-trial-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(8, 35, 29, 0.62);
        backdrop-filter: blur(8px);
      }
      .hs-elite-trial-modal__dialog {
        position: relative;
        isolation: isolate;
        width: min(100%, 620px);
        border-radius: 28px;
        overflow: hidden;
        background:
          radial-gradient(circle at 18% 18%, rgba(255,255,255,0.62), transparent 34%),
          radial-gradient(circle at 80% 78%, rgba(130, 250, 182, 0.16), transparent 36%),
          linear-gradient(135deg, #f7fbf7 0%, #e6f2ea 42%, #d4eadf 100%);
        border: 1px solid rgba(255,255,255,0.72);
        box-shadow: 0 30px 90px rgba(8, 35, 29, 0.24);
        color: #113b30;
      }
      .hs-elite-trial-modal__body {
        position: relative;
        z-index: 1;
        padding: 30px;
        display: grid;
        gap: 18px;
      }
      .hs-elite-trial-modal__eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        width: fit-content;
        padding: 10px 16px;
        border-radius: 999px;
        background: #133f30;
        color: #f4fbf7;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .hs-elite-trial-modal__eyebrow::before {
        content: "";
        width: 10px;
        height: 10px;
        border-radius: 999px;
        background: #f47a1f;
        box-shadow: 0 0 0 5px rgba(244,122,31,0.14);
      }
      .hs-elite-trial-modal__dialog h2 {
        margin: 0;
        font-size: clamp(32px, 4vw, 48px);
        line-height: 0.98;
        letter-spacing: -0.04em;
        color: #0f3428;
      }
      .hs-elite-trial-modal__dialog p {
        margin: 0;
        color: rgba(17, 59, 48, 0.82);
        font-size: 18px;
        line-height: 1.55;
      }
      .hs-elite-trial-modal__list {
        display: grid;
        gap: 10px;
        margin: 0;
        padding: 0;
        list-style: none;
      }
      .hs-elite-trial-modal__list li {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        font-size: 16px;
        line-height: 1.5;
        color: #133f30;
      }
      .hs-elite-trial-modal__list li::before {
        content: "✓";
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        flex: 0 0 22px;
        border-radius: 999px;
        background: rgba(19, 63, 48, 0.09);
        color: #1b5b46;
        font-size: 13px;
        font-weight: 800;
        margin-top: 1px;
      }
      .hs-elite-trial-modal__note {
        border-radius: 18px;
        padding: 16px 18px;
        background: rgba(255,255,255,0.72);
        color: rgba(17, 59, 48, 0.78);
        font-size: 14px;
        line-height: 1.6;
      }
      .hs-elite-trial-modal__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      .hs-elite-trial-modal__btn {
        appearance: none;
        border: 1px solid transparent;
        border-radius: 16px;
        padding: 14px 22px;
        font-size: 16px;
        font-weight: 800;
        line-height: 1;
        cursor: pointer;
        text-decoration: none;
        transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
      }
      .hs-elite-trial-modal__btn:hover {
        transform: translateY(-1px);
      }
      .hs-elite-trial-modal__btn--primary {
        background: linear-gradient(180deg, #ff8a30 0%, #f47a1f 55%, #df650c 100%);
        color: #fff;
        box-shadow: 0 18px 34px rgba(223, 101, 12, 0.22);
      }
      .hs-elite-trial-modal__btn--secondary {
        background: rgba(255,255,255,0.78);
        border-color: rgba(17,59,48,0.10);
        color: #133f30;
      }
      .hs-elite-trial-modal__close {
        position: absolute;
        top: 18px;
        right: 18px;
        z-index: 3;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 999px;
        border: 1px solid rgba(17,59,48,0.10);
        background: rgba(255,255,255,0.84);
        color: #133f30;
        font-size: 18px;
        cursor: pointer;
        pointer-events: auto;
      }
      @media (max-width: 640px) {
        .hs-elite-trial-modal { padding: 14px; }
        .hs-elite-trial-modal__body { padding: 24px 20px 20px; }
        .hs-elite-trial-modal__dialog p { font-size: 16px; }
        .hs-elite-trial-modal__actions {
          display: grid;
          grid-template-columns: 1fr;
        }
        .hs-elite-trial-modal__btn {
          width: 100%;
          text-align: center;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const createModal = (payload, options = {}) => {
    const { persistDismiss = true } = options;
    injectStyles();

    const existing = document.getElementById("hs-elite-trial-modal");
    if (existing) existing.remove();

    const modal = document.createElement("div");
    modal.id = "hs-elite-trial-modal";
    modal.className = "hs-elite-trial-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML = `
      <div class="hs-elite-trial-modal__backdrop" data-close></div>
      <div class="hs-elite-trial-modal__dialog">
        <button class="hs-elite-trial-modal__close" type="button" aria-label="Close trial offer" data-close>&times;</button>
        <div class="hs-elite-trial-modal__body">
          <span class="hs-elite-trial-modal__eyebrow">30-day free trial</span>
          <h2>Try Elite free for 30 days.</h2>
          <p>Unlock discounted member shipping rates and keep the same label workflow you already use today.</p>
          <ul class="hs-elite-trial-modal__list">
            <li>Discounted Elite shipping rates for 30 days.</li>
            <li>A primary card is required now so billing can begin only if you keep Elite after the trial ends.</li>
            <li>Cancel from Subscription Management before the renewal date and Stripe will not charge the next cycle.</li>
          </ul>
          <div class="hs-elite-trial-modal__note">
            ${payload.hasPrimaryCard
              ? "You already have a billing card on file, and Stripe Checkout will ask you to confirm it before the 30-day trial begins."
              : "You do not have a billing card on file yet, so Stripe Checkout will ask for one before the 30-day trial begins."}
          </div>
          <div class="hs-elite-trial-modal__actions">
            <a class="hs-elite-trial-modal__btn hs-elite-trial-modal__btn--primary" href="${config.startTrialUrl}?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}">Start free 30-day trial</a>
            <button class="hs-elite-trial-modal__btn hs-elite-trial-modal__btn--secondary" type="button" data-close>Do later</button>
          </div>
        </div>
      </div>
    `;

    const close = () => {
      if (persistDismiss && accountKey) {
        resetAutoPromptCooldown();
      }
      modal.remove();
      document.documentElement.classList.remove("hs-elite-trial-open");
      document.body.classList.remove("hs-elite-trial-open");
      document.removeEventListener("keydown", onKeydown);
    };

    const onKeydown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    modal.addEventListener("click", (event) => {
      const control = event.target.closest("[data-close]");
      if (!control) return;
      event.preventDefault();
      close();
    });

    document.addEventListener("keydown", onKeydown);
    document.documentElement.classList.add("hs-elite-trial-open");
    document.body.classList.add("hs-elite-trial-open");
    document.body.appendChild(modal);
  };

  const fetchMemberStatus = () => {
    if (!hasSignedInConfig) return Promise.resolve(null);
    if (memberStatusPromise) return memberStatusPromise;

    memberStatusPromise = fetch(config.memberStatusUrl, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({
        email: config.email,
        firstName: config.firstName || "",
        lastName: config.lastName || "",
        phone: config.phone || "",
      }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.ok === false) return null;
        return payload;
      })
      .catch(() => null);

    return memberStatusPromise;
  };

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(trialTriggerSelector);
    if (!trigger || !hasSignedInConfig) return;

    event.preventDefault();
    fetchMemberStatus().then((payload) => {
      if (payload?.trialPromptEligible) {
        createModal(payload, { persistDismiss: false });
        return;
      }

      if (trigger.href) {
        window.location.href = trigger.href;
      }
    });
  });

  if (!hasSignedInConfig || !accountKey) return;
  if (consumeAutoPromptCooldown()) return;

  fetchMemberStatus().then((payload) => {
    if (!payload || !payload.trialPromptEligible) return;
    resetAutoPromptCooldown();
    createModal(payload);
  });
})();
